import { useTranslation } from 'react-i18next';
import { Container, PageTitle, CommonFlex, CommonText } from '../../styledComponents/common';
import { ColoredButtonWithIcon, ButtonIcon } from '../../styledComponents/buttons';
import SyncIcon from 'assets/img/common/sync.svg';
import { TablesContainer, TablesWrapper, TableBox, TableTitle, TableHeader, NameCell, CountCell, TableRow } from './checkHandsStyles';
import styled from 'styled-components';
import { useEffect, useMemo } from 'react';
import ComponentCircleLoader from '../../components/Loaders/ComponentCircleLoader';
import { useHistory } from 'react-router-dom';

// Styled component for warning text
const WarningText = styled.span`
  color: #ff6b6b; // Warning color (red)
  font-weight: 600;
`;

// Styled component for loading container
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 300px;
`;

const CheckHands = ({ checkHandsLoading, checkHandsData, checkHandsError, getCheckHandsData }) => {
  const { t } = useTranslation();
  const userRole = localStorage.getItem('user_role');
  const history = useHistory();

  // Redirect non-admin users
  useEffect(() => {
    if (userRole !== 'admin') {
      history.push('/');
    }
  }, [userRole, history]);

  // Define the role order
  const roleOrder = ['gm', 'agm', 'admin', 'supervisor', 'employee'];

  // Fetch data on component mount
  useEffect(() => {
    getCheckHandsData();
  }, [getCheckHandsData]);

  // Function to handle sync button click
  const handleSync = () => {
    getCheckHandsData();
  };

  // Function to format role names
  const formatRoleName = (role) => {
    if (role === 'gm' || role === 'agm') {
      return role.toUpperCase();
    } else {
      return role.charAt(0).toUpperCase() + role.slice(1);
    }
  };

  // Process data to ensure all roles exist in both tables and sort by specified order
  const processedData = useMemo(() => {
    if (!checkHandsData) return null;

    // Create a copy of the original data
    const processed = {
      albait: [...checkHandsData.albait],
      apploye: [...checkHandsData.apploye]
    };

    // Get all unique roles from both sources
    const allRoles = new Set();
    processed.albait.forEach(item => allRoles.add(item.role));
    processed.apploye.forEach(item => allRoles.add(item.role));

    // Add missing roles to albait with count 0
    allRoles.forEach(role => {
      if (!processed.albait.some(item => item.role === role)) {
        processed.albait.push({ role, TotalCount: 0 });
      }
    });

    // Add missing roles to apploye with count 0
    allRoles.forEach(role => {
      if (!processed.apploye.some(item => item.role === role)) {
        processed.apploye.push({ role, TotalCount: 0 });
      }
    });

    // Sort both arrays by the specified role order
    const sortByRoleOrder = (a, b) => {
      const indexA = roleOrder.indexOf(a.role);
      const indexB = roleOrder.indexOf(b.role);

      // If both roles are in the order array, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only one role is in the order array, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      // If neither role is in the order array, sort alphabetically
      return a.role.localeCompare(b.role);
    };

    processed.albait.sort(sortByRoleOrder);
    processed.apploye.sort(sortByRoleOrder);

    return processed;
  }, [checkHandsData, roleOrder]);

  // Function to check if counts match between apploye and albait for the same role
  const countsMatch = (role, count, dataSource) => {
    if (!processedData) return true;

    const otherSource = dataSource === 'apploye' ? 'albait' : 'apploye';

    // Find matching role in the other data source
    const matchingItem = processedData[otherSource].find(item => item.role === role);

    // If counts don't match, return false
    if (matchingItem.TotalCount !== count) {
      return false;
    }

    return true;
  };

  // Function to render text with warning color if needed
  const renderWithWarning = (text, role, count, dataSource) => {
    if (!countsMatch(role, count, dataSource)) {
      return <WarningText>{text}</WarningText>;
    }
    return text;
  };

  return (
    <div className="content">
      <PageTitle>{t('check_hands')}</PageTitle>
      <Container padding="0">
        <CommonFlex
          justifyContent="space-between"
          alignItems="center"
          margin="20px 0 20px 0"
          padding="0 20px 0 0"
        >
          {/* Left side - empty or you can add a title here */}
          <div></div>

          {/* Right side - sync button */}
          <ColoredButtonWithIcon
            onClick={handleSync}
            margin="0 20px 0 0"
            disabled={checkHandsLoading}
          >
            <ButtonIcon src={SyncIcon} alt="" />
            {checkHandsLoading ? t('syncing') : t('sync')}
          </ColoredButtonWithIcon>
        </CommonFlex>

        {checkHandsLoading ? (
          <LoadingContainer>
            <ComponentCircleLoader />
          </LoadingContainer>
        ) : checkHandsError ? (
          <LoadingContainer>
            <CommonText color="#ff6b6b">{checkHandsError}</CommonText>
          </LoadingContainer>
        ) : processedData ? (
          /* Centered Container for Tables */
          <TablesContainer>
            {/* Tables Container with specified dimensions */}
            <TablesWrapper>
              {/* First Table - Albait */}
              <TableBox>
                {/* Table Title */}
                <TableTitle>
                  <CommonText fontWeight="600">{t('albait_guests')}</CommonText>
                </TableTitle>

                {/* Table Header */}
                <TableHeader>
                  <NameCell>{t('name')}</NameCell>
                  <CountCell>{t('total_count')}</CountCell>
                </TableHeader>

                {/* Table Rows */}
                {processedData.albait.map((item, index) => (
                  <TableRow key={index}>
                    <NameCell>
                      {renderWithWarning(
                        formatRoleName(item.role),
                        item.role,
                        item.TotalCount,
                        'albait'
                      )}
                    </NameCell>
                    <CountCell>
                      {renderWithWarning(
                        item.TotalCount,
                        item.role,
                        item.TotalCount,
                        'albait'
                      )}
                    </CountCell>
                  </TableRow>
                ))}
              </TableBox>

              {/* Second Table - Apploye */}
              <TableBox>
                {/* Table Title */}
                <TableTitle>
                  <CommonText fontWeight="600">{t('apploye')}</CommonText>
                </TableTitle>

                {/* Table Header */}
                <TableHeader>
                  <NameCell>{t('name')}</NameCell>
                  <CountCell>{t('total_count')}</CountCell>
                </TableHeader>

                {/* Table Rows */}
                {processedData.apploye.map((item, index) => (
                  <TableRow key={index}>
                    <NameCell>
                      {renderWithWarning(
                        formatRoleName(item.role),
                        item.role,
                        item.TotalCount,
                        'apploye'
                      )}
                    </NameCell>
                    <CountCell>
                      {renderWithWarning(
                        item.TotalCount,
                        item.role,
                        item.TotalCount,
                        'apploye'
                      )}
                    </CountCell>
                  </TableRow>
                ))}
              </TableBox>
            </TablesWrapper>
          </TablesContainer>
        ) : null}
      </Container>
    </div>
  );
};

export default CheckHands;